import type { Optional } from '@festina-life/core'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'

import { isSameTabTarget } from './isSameTabTarget'
import type { TabLink } from './types'

type Props = {
  storedTabLinks: Optional<TabLink[]>
  /** The localStorage key the tab links are stored under. */
  tabsStorageKey: string
  updateTabLinks: (nextValue: TabLink[]) => void
  /** The current route as a tab link, or nothing when it must not be tracked as a tab. */
  currentPathAsTabLink: Optional<TabLink>
}

type Visit = { tabsStorageKey: string; tabLink: TabLink }

export const useEnsureCurrentRouteInTabLinks = ({
  storedTabLinks,
  tabsStorageKey,
  updateTabLinks,
  currentPathAsTabLink: currentPathAsLink,
}: Props) => {
  const lastVisitRef = useRef<Optional<Visit>>(null)

  useEffect(
    function ensureCurrentRouteInTabLinks() {
      let shouldTabsBeUpdated = false

      if (currentPathAsLink) {
        const lastVisit = lastVisitRef.current
        const isSameVisit =
          lastVisit?.tabsStorageKey === tabsStorageKey &&
          isSameTabTarget(lastVisit.tabLink, currentPathAsLink)
        lastVisitRef.current = { tabsStorageKey, tabLink: currentPathAsLink }

        const nextTabLinks = structuredClone(storedTabLinks ?? [])
        const tabLinkForCurrentRoute = nextTabLinks?.find((tabLink) =>
          isSameTabTarget(tabLink, currentPathAsLink),
        )

        // The tab was closed while its route is still current: the navigation away
        // from it has not been applied yet, so it must not be re-added.
        if (!tabLinkForCurrentRoute && isSameVisit) {
          return
        }

        const activeTab = nextTabLinks?.find((tabLink) => tabLink.isActive)
        const isActiveTabDifferent =
          activeTab &&
          (activeTab.id !== currentPathAsLink.id ||
            // Abstract equals operator to handle null and undefined
            activeTab.layer.tenantId != currentPathAsLink.layer.tenantId ||
            activeTab.layer.layerId != currentPathAsLink.layer.layerId ||
            activeTab.block != currentPathAsLink.block ||
            activeTab.variant !== currentPathAsLink.variant ||
            activeTab.isDraft !== currentPathAsLink.isDraft)

        if (isActiveTabDifferent) {
          activeTab.isActive = false
          shouldTabsBeUpdated = true
        }

        // Read in the effect rather than during render: the React Compiler caches
        // dependency-less render values, which would freeze the timestamp.
        const now = dayjs().valueOf()

        if (!tabLinkForCurrentRoute) {
          nextTabLinks.push({ ...currentPathAsLink, lastVisitedTimestamp: now })
          shouldTabsBeUpdated = true
        } else if (
          !tabLinkForCurrentRoute.isActive ||
          tabLinkForCurrentRoute.isDraft !== currentPathAsLink.isDraft ||
          (tabLinkForCurrentRoute.label == null &&
            currentPathAsLink.label != null) ||
          tabLinkForCurrentRoute.annulledEntityVariant !==
            currentPathAsLink.annulledEntityVariant
        ) {
          tabLinkForCurrentRoute.isActive = true
          tabLinkForCurrentRoute.isDraft = currentPathAsLink.isDraft
          tabLinkForCurrentRoute.label = currentPathAsLink.label
          tabLinkForCurrentRoute.annulledEntityVariant =
            currentPathAsLink.annulledEntityVariant
          tabLinkForCurrentRoute.lastVisitedTimestamp = now
          shouldTabsBeUpdated = true
        }

        if (shouldTabsBeUpdated) {
          updateTabLinks(nextTabLinks)
        }
      }
    },
    [currentPathAsLink, updateTabLinks, storedTabLinks, tabsStorageKey],
  )
}
